import { hasCentScale, isNegative, isZero, money } from '@fameacc/money';
import { z } from 'zod';
/**
 * Skalenprüfung an der Servicegrenze. Es wird NICHT gerundet:
 * numeric(15,2) rundet den Wert, bevor irgendein CHECK ihn sieht, und die
 * Anwendungsprüfung über die vollen Werte wäre grün, während die
 * gespeicherten Werte unausgeglichen sind. Gerundet wird ausschliesslich am
 * Rundungspunkt.
 *
 * **Alles in einer `.transform()`, und nach jedem `addIssue` ein
 * `return z.NEVER`.** Beides ist notwendig, aus zwei verschiedenen Gründen:
 *
 * * `.superRefine()` wäre hier falsch. Sein Rückgabewert ist als `void` typisiert
 *   und wird zur Laufzeit verworfen. Ein damit gebautes Schema gäbe die **rohe**
 *   Eingabe zurück — `"10.100"` bliebe `"10.100"` und ginge unkanonisiert in die
 *   Datenbank, obwohl die Prüfung grün war.
 * * `ctx.addIssue()` ohne `fatal` markiert das Ergebnis nur als verschmutzt; die
 *   Kette läuft weiter. Ohne die frühe Rückkehr erreichte eine ungültige Eingabe
 *   den nächsten Schritt, dort riefe `money()` mit Unsinn auf — und `money()`
 *   **wirft**. Die Ausnahme entkäme dem `safeParse`, und aus einer 400 würde eine
 *   500. `z.NEVER` gibt es genau für diesen Ausstieg.
 */
function parseAmount(value, ctx, opts = {}) {
    let amount;
    try {
        amount = money(value);
    }
    catch {
        // Der Text der MoneyParseError beschreibt die kanonische Form und gehört
        // nicht in eine HTTP-Antwort. Die Meldung hier ist die des Vertrags.
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Kein gültiger Geldbetrag' });
        return z.NEVER;
    }
    // Vor dem Vorzeichen geprüft: Wer "-10.105" schickt, hat zwei Fehler, aber
    // der interessante ist die Skala — sie entscheidet, ob der Wert überhaupt
    // buchbar ist.
    if (!hasCentScale(amount)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            // `params` trägt das maschinenlesbare Merkmal, an dem der Dienst diesen
            // einen Fall erkennt. Über den Meldungstext ginge es auch — bis zur
            // ersten Umformulierung. Die Skalenprüfung ist der einzige Fall, der ein
            // eigenes Merkmal braucht: Sie hat als einzige keinen Rückhalt in der
            // Datenbank (numeric(15,2) rundet, bevor ein CHECK den Wert sieht) und
            // trägt deshalb einen eigenen fachlichen Code.
            params: { code: 'AMOUNT_SCALE_INVALID' },
            message: 'Höchstens zwei Nachkommastellen zulässig',
        });
        return z.NEVER;
    }
    if (opts.positive === true && (isZero(amount) || isNegative(amount))) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Betrag muss größer als null sein' });
        return z.NEVER;
    }
    if (opts.nonNegative === true && isNegative(amount)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Betrag darf nicht negativ sein' });
        return z.NEVER;
    }
    return amount;
}
/**
 * Ein Geldbetrag in Centgenauigkeit. Vorzeichen erlaubt.
 *
 * Das Ergebnis ist ein `Money`, kein `string`: Ab hier trägt der Wert seine
 * Marke, und der Übersetzer verhindert, dass jemand ihn mit einer beliebigen
 * Zeichenkette verwechselt.
 */
export const centAmount = z.string().transform((v, ctx) => parseAmount(v, ctx));
/** Ein Betrag echt über null. Für Buchungszeilen: eine Zeile über 0,00 € ist keine. */
export const positiveAmount = z.string().transform((v, ctx) => parseAmount(v, ctx, { positive: true }));
/** Ein Betrag ab null. Für Steuerbeträge: 0,00 € ist zulässig, minus nicht. */
export const nonNegativeAmount = z
    .string()
    .transform((v, ctx) => parseAmount(v, ctx, { nonNegative: true }));
/**
 * Kalendertag ohne Zeitanteil.
 *
 * Bewusst als Zeichenkette und nicht als `Date`: Ein `Date` trägt eine Uhrzeit
 * und eine Zeitzone mit sich, und beim Buchungsdatum führt beides nur dazu,
 * dass eine Buchung am Monatsersten in Berlin am Monatsletzten in UTC landet.
 *
 * **Exportiert ab Phase 3**, damit `documents.ts` denselben Baustein benutzt
 * statt eines zweiten mit derselben Absicht. Zwei Datumsmuster in einem Paket
 * wären zwei Auslegungen davon, was ein Belegdatum ist.
 */
export const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum im Format JJJJ-MM-TT erwartet');
/** Eine UUID, wie sie Buchungen, Belege und Vorgänge identifiziert. */
export const uuid = z.string().uuid('Keine gültige UUID');
/** Soll oder Haben. Eine Zeile steht auf genau einer Seite. */
export const postingSideSchema = z.enum(['debit', 'credit']);
/**
 * Eine Buchungszeile.
 *
 * `amount` ist echt positiv: Die Seite steckt in `side`, nicht im Vorzeichen.
 * Ein negativer Betrag auf der Sollseite wäre eine zweite, stille Art, Haben zu
 * schreiben — und zwei Schreibweisen für dieselbe Sache sind der Anfang einer
 * unausgeglichenen Buchung.
 */
export const postingLineSchema = z.object({
    accountNumber: z
        .string()
        .regex(/^[0-9]{4,6}$/, 'Kontonummer besteht aus vier bis sechs Ziffern'),
    side: postingSideSchema,
    amount: positiveAmount,
    taxKey: z.string().min(1, 'Steuerschlüssel darf nicht leer sein').optional(),
    taxAmount: nonNegativeAmount.optional(),
    description: z.string().optional(),
    /**
     * Der Kunde, dem diese Zeile gehört — der offene Posten.
     *
     * Neu in Phase 3. `journal_line.customer_id` steht seit Migration 0018 und war
     * bis hierher durchgehend `NULL`: Es gab keinen Pfad, der einen Kunden an eine
     * Journalzeile bindet. Die Belegbuchung ist der erste (Spec 8.5, Sollzeile auf
     * dem Forderungskonto), und ohne dieses Feld könnte sie ihn nicht setzen — die
     * Spalte bliebe leer und der offene Posten aus Phase 8 hätte keine Grundlage.
     *
     * Optional und **ohne** Bedingung, die sie auf ein Forderungskonto zwingt:
     * Guthabenbewegungen laufen kundenbezogen über „Erhaltene Anzahlungen", und ab
     * Phase 6 kämen weitere Konten hinzu (Migration 0018).
     */
    customerId: uuid.optional(),
});
/**
 * Eine Buchungsanfrage.
 *
 * `lines` verlangt mindestens zwei Einträge. Der Ausgleich Soll gegen Haben
 * wird hier **nicht** geprüft — das tut der Trigger in der Datenbank beim
 * Commit (Durchsetzungsfall E-7). Eine Prüfung an zwei Stellen hiesse, zwei
 * Wahrheiten zu pflegen; die maßgebliche steht dort, wo niemand an ihr
 * vorbeikommt.
 *
 * **`.strict()` seit `D-postingstrict` (2026-08-29).** Bis dahin war dies das
 * einzige der achtzehn über `pruefeRumpf`/`parse` gebundenen Rumpfschemas ohne
 * `.strict()` — eine Bestandsprüfung fand dafür keinen Grund mehr: Kein
 * interner Aufrufer (`kontierung/buchung.ts`,
 * `documents/document-posting-builder.ts`, `guthaben/*`) geht über dieses
 * Schema — sie rufen `JournalService.post()` in-process mit einem bereits
 * geprüften `PostingRequest` auf und lassen die HTTP-Grenze aus. Die einzige
 * Prüfstelle ist `journal.controller.ts::post()`, und dort ist der Rumpf, der
 * ankommt, immer genau diese neun Felder — die Weboberfläche bucht gar nicht
 * über diesen Weg, und jeder Testaufrufer (`apps/api/test/**`) schickt einen
 * Rumpf ohne Zusatzfelder. Ein vertippter Feldname wäre damit vorher still
 * verschluckt worden statt als `VALIDATION_FAILED` zurückzukommen — derselbe
 * Fehler, den `.strict()` bei den anderen siebzehn schon abfängt.
 */
export const postingRequestSchema = z
    .object({
    bookingDate: isoDate,
    valueDate: isoDate.optional(),
    description: z.string().trim().min(1, 'Beschreibung darf nicht leer sein'),
    lines: z.array(postingLineSchema).min(2, 'Eine Buchung hat mindestens zwei Zeilen'),
    sourceSystem: z.string().min(1, 'Quellsystem darf nicht leer sein'),
    sourceReference: z.string().optional(),
    // Optional, aber wenn angegeben, dann mit Inhalt: Ein leerer
    // Idempotenzschlüssel wäre für jede Anfrage derselbe und machte aus der
    // Wiederholungssicherung eine Sperre.
    idempotencyKey: z.string().min(1, 'Idempotenzschlüssel darf nicht leer sein').optional(),
    correlationId: uuid,
    documentId: uuid.optional(),
})
    .strict();
/**
 * Ein Storno. Die Gegenbuchung selbst erzeugt der Dienst.
 *
 * **`.strict()` seit `I-reversalstrict` (2026-08-29), Konsistenz mit
 * `postingRequestSchema` (`D-postingstrict`, s.o.).** Dieselbe Bestandsprüfung,
 * dasselbe Ergebnis: Kein interner Aufrufer — `journal.controller.ts::post()`
 * ist der einzige HTTP-Rand, `documents/document.service.ts::reverseWithin()`
 * und `kontierung/kontierung.service.ts::reverseWithin()` rufen in-process mit
 * einem bereits zusammengestellten Rumpf aus genau den fünf Feldern unten
 * auf — reicht ein Feld mit, das hier nicht steht. Jeder Testaufrufer
 * (`apps/api/test/journal/reverse.test.ts`, `documents/cancel.test.ts`,
 * `berichte/offene-posten.test.ts`, `journal/lock-order.test.ts`) schickt
 * ebenfalls nur `entryId`/`reason`/`correlationId`/`idempotencyKey`/
 * `documentId`. Ein vertippter Feldname wäre vorher still verschluckt worden
 * statt als `VALIDATION_FAILED` zurückzukommen.
 */
export const reversalRequestSchema = z
    .object({
    entryId: uuid,
    // Die Begründung ist Pflicht und trägt die Nachvollziehbarkeit: Ein Storno
    // ohne Grund ist nach GoBD eine Änderung ohne Spur.
    reason: z.string().trim().min(1, 'Begründung darf nicht leer sein'),
    correlationId: uuid,
    idempotencyKey: z.string().min(1, 'Idempotenzschlüssel darf nicht leer sein').optional(),
    /**
     * Der STORNOBELEG, dessen Gegenbuchung diese ist.
     *
     * Neu in Phase 3 (Spec 8.4). Bis hierher setzte `reverse()` das Feld fest auf
     * `null`, weil es keine Belege gab. Ohne diese Zeile könnte der Stornobeleg
     * seine Gegenbuchung nicht tragen — und `assert_journal_document_reversal`
     * wiese jeden Storno einer Belegbuchung ab, also genau den Fall, für den er
     * gebaut ist.
     */
    documentId: uuid.optional(),
})
    .strict();
// --- Antwortverträge (V-vertrag3) ------------------------------------------
/**
 * Eine Buchung ohne ihre Zeilen — `apps/api/src/journal/journal.query.ts`,
 * `JournalEntryView`. Grundlage von `GET /api/v1/journal/entries` und
 * `GET /api/v1/journal/entries/:id` (dort erweitert, siehe
 * `journalEntryDetailSchema`).
 */
export const journalEntryViewSchema = z
    .object({
    id: uuid,
    /** `bigint` und deshalb als Zeichenkette. */
    entryNo: z.string(),
    bookingDate: isoDate,
    valueDate: isoDate.nullable(),
    description: z.string(),
    sourceSystem: z.string(),
    sourceReference: z.string().nullable(),
    documentId: uuid.nullable(),
    /** Gesetzt, wenn diese Buchung ein Storno ist. */
    reversesEntryId: uuid.nullable(),
    periodId: uuid,
    correlationId: uuid,
    createdAt: z.string(),
    createdBy: uuid,
    /** Die Summe der Sollseite — bei einer ausgeglichenen Buchung ihr Betrag. */
    amount: centAmount,
})
    .strict();
/**
 * Die Antworthülle von `GET /api/v1/journal/entries` — `{ entries: [...] }`. Der Umschlag lebt neben seinem
 * Elementschema, damit Server und Oberfläche dieselbe Hülle prüfen (Handbuch §3.969).
 */
export const journalEntryListResponseSchema = z.object({ entries: z.array(journalEntryViewSchema).readonly() }).strict();
/** Eine Buchungszeile — `JournalLineView`. */
export const journalLineViewSchema = z
    .object({
    position: z.number().int().positive(),
    accountNumber: z.string(),
    accountName: z.string(),
    /** Beide Seiten stehen da, eine davon auf null — so liegt die Zeile in der Datenbank. */
    debit: centAmount,
    credit: centAmount,
    taxKey: z.string().nullable(),
    taxAmount: centAmount,
    description: z.string().nullable(),
})
    .strict();
/**
 * Eine Buchung samt ihrer Zeilen — `JournalEntryDetail`,
 * `GET /api/v1/journal/entries/:id`.
 */
export const journalEntryDetailSchema = journalEntryViewSchema
    .extend({ lines: z.array(journalLineViewSchema) })
    .strict();
/**
 * Was eine Buchung zurückmeldet — `PostingResult`,
 * `POST /api/v1/journal/entries` und `POST /api/v1/journal/entries/:id/reverse`.
 */
export const postingResultSchema = z
    .object({
    id: uuid,
    /** `entry_no` ist ein `bigint` und wird als Zeichenkette geführt — immer. */
    entryNo: z.string(),
    /** `true`, wenn der Idempotenzschlüssel bereits vorlag und nichts geschrieben wurde. */
    deduplicated: z.boolean(),
})
    .strict();
//# sourceMappingURL=posting.js.map